/**
 * Scale and crop a raw Base64 image string to fixed dimensions.
 * - Portrait: scale width to targetWidth, then crop vertically.
 * - Landscape: scale height to targetHeight, then crop horizontally.
 * Aspect ratio is preserved during scaling.
 *
 * @param rawBase64 - Source image as raw Base64 string (no prefix).
 * @param targetWidth - Desired output width (default 320).
 * @param targetHeight - Desired output height (default 240).
 * @param offsetX - Optional x offset for cropping (landscape).
 * @param offsetY - Optional y offset for cropping (portrait).
 * @returns Promise<string> Raw Base64 string of the cropped image.
 */
export function cropBase64Image(
    rawBase64: string,
    targetWidth: number = 320,
    targetHeight: number = 240,
    offsetX?: number,
    offsetY?: number
): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                const portrait = img.naturalHeight > img.naturalWidth;

                // Step 1: scale with aspect ratio preserved
                let scaledWidth: number;
                let scaledHeight: number;

                if (portrait) {
                    scaledWidth = targetWidth;
                    scaledHeight = (img.naturalHeight / img.naturalWidth) * targetWidth;
                } else {
                    scaledHeight = targetHeight;
                    scaledWidth = (img.naturalWidth / img.naturalHeight) * targetHeight;
                }

                const scaleCanvas = document.createElement("canvas");
                scaleCanvas.width = scaledWidth;
                scaleCanvas.height = scaledHeight;
                const scaleCtx = scaleCanvas.getContext("2d");
                if (!scaleCtx) {
                    reject(new Error("Canvas 2D context not available"));
                    return;
                }
                scaleCtx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

                // Step 2: crop to target dimensions
                const cropCanvas = document.createElement("canvas");
                cropCanvas.width = targetWidth;
                cropCanvas.height = targetHeight;
                const cropCtx = cropCanvas.getContext("2d");
                if (!cropCtx) {
                    reject(new Error("Canvas 2D context not available"));
                    return;
                }

                let sx = 0;
                let sy = 0;

                if (portrait) {
                    // crop vertically, honour offsetY
                    sy =
                        typeof offsetY === "number"
                            ? Math.max(0, Math.min(offsetY, scaledHeight - targetHeight))
                            : (scaledHeight - targetHeight) / 2;
                } else {
                    // crop horizontally, honour offsetX
                    sx =
                        typeof offsetX === "number"
                            ? Math.max(0, Math.min(offsetX, scaledWidth - targetWidth))
                            : (scaledWidth - targetWidth) / 2;
                }

                cropCtx.drawImage(
                    scaleCanvas,
                    sx, sy, targetWidth, targetHeight, // source rect
                    0, 0, targetWidth, targetHeight    // destination rect
                );

                const dataUrl = cropCanvas.toDataURL("image/jpeg");
                const rawResult = dataUrl.replace(/^data:image\/jpeg;base64,/, "");
                resolve(rawResult);
            } catch (err) {
                reject(err);
            }
        };

        img.onerror = () => reject(new Error("Failed to load base64 image"));
        img.src = `data:image/jpeg;base64,${rawBase64}`;
    });
}

export async function extractFrames(
    video: HTMLVideoElement,
    frameCount: number,
    startIndex: number = 0,
    step: number = 1
): Promise<string[]> {
    const frames: string[] = [];

    const capture = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL("image/png");
    };

    const duration = video.duration;
    const interval = duration / (frameCount + startIndex * step + 1);

    for (let i = 0; i < frameCount; i++) {
        const index = startIndex + i * step;
        const time = interval * index;

        await new Promise<void>((resolve) => {
            const handler = () => {
                resolve();
                video.removeEventListener("seeked", handler);
            };
            video.addEventListener("seeked", handler);
            video.currentTime = time;
        });

        const frame = capture();
        if (frame) frames.push(frame);
    }

    return frames;
}

export async function extractEquidistantFrames(
    video: HTMLVideoElement,
    count: number = 24
): Promise<string[]> {
    if (!video.duration || isNaN(video.duration)) {
        // Ensure metadata is loaded
        await new Promise<void>((resolve) => {
            video.addEventListener("loadedmetadata", () => resolve(), {
                once: true,
            });
        });
    }

    const duration = video.duration;
    const frames: string[] = [];

    // Create a single reusable canvas
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Unable to get 2D context");

    // Helper to seek and wait
    const seekTo = (time: number) =>
        new Promise<void>((resolve) => {
            const handler = () => {
                video.removeEventListener("seeked", handler);
                resolve();
            };
            video.addEventListener("seeked", handler);
            video.currentTime = time;
        });

    for (let i = 0; i < count; i++) {
        const t = (duration * i) / (count - 1); // inclusive of last frame
        await seekTo(t);

        ctx.drawImage(video, 0, 0);
        frames.push(canvas.toDataURL("image/png"));
    }

    return frames;
}

export function computeGridTransform(
    index: number,
    columns = 6,
    rows = 4,
    margin = 5
) {
    const col = index % columns;
    const row = Math.floor(index / columns);

    return {
        col,
        row,
        translateX: `calc(${col} * (100% / ${columns}) + ${col * margin}px)`,
        translateY: `calc(${row} * (100% / ${rows}) + ${row * margin}px)`
    };
}

export function remapIndex(i: number): number {
    const map = [
        3,  // 0 -> 3
        0,  // 1 -> 0
        4,  // 2 -> 4
        1,  // 3 -> 1
        5,  // 4 -> 5
        2,  // 5 -> 2
        9,  // 6 -> 9
        6,  // 7 -> 6
        10, // 8 -> 10
        7,  // 9 -> 7
        11, // 10 -> 11
        8,  // 11 -> 8
        15, // 12 -> 15
        12, // 13 -> 12
        16, // 14 -> 16
        13, // 15 -> 13
        17, // 16 -> 17
        14, // 17 -> 14
        21, // 18 -> 21
        18, // 19 -> 18
        22, // 20 -> 22
        19, // 21 -> 19
        23, // 22 -> 23
        20  // 23 -> 20
    ];

    return map[i];
}

export function getAspectRatioFromBase64(base64: string): Promise<number> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve(img.naturalWidth / img.naturalHeight);
        };
        img.onerror = reject;
        img.src = base64;
    });
}

export function equidistantFrames(items: string[], n:number): string[] {
    if (items.length < n) {
        throw new Error("Input array must contain at least 80 items.");
    }

    const result: string[] = [];
    const step = (items.length - 1) / (n - 1);

    for (let i = 0; i < n; i++) {
        const sourceIndex = Math.round(i * step);
        result.push(items[sourceIndex]);
    }

    return result;
}

